import { Form, Input, Button, Typography, Card, Divider, App } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import API from "../../../services/api";
import { auth, provider } from "../../../firebase";
import { signInWithPopup } from "firebase/auth";
import "./Login.css";
import { useAuth } from "../../../context/AuthContext";

const { Title, Text } = Typography;

function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { setUser } = useAuth();
  const [form] = Form.useForm();

  const { message } = App.useApp();

  const onFinish = async (values) => {
    if (!navigator.onLine) {
      message.error("You are offline. Check your internet connection.");
      return;
    }
    try {
      setLoading(true);
      message.destroy();

      const res = await API.post("/users/login", values, {
        withCredentials: true,
      });

      localStorage.setItem("token", res.data.token);
      const cleanUser = {
        ...res.data.user,
        profileImg:
          res.data.user.loginProvider === "google"
            ? res.data.user.profileImg || null
            : null,
      };

      localStorage.setItem("user", JSON.stringify(cleanUser));
      setUser(cleanUser);
      message.success("Login successful");
      navigate("/");
    } catch (error) {
      message.destroy();
      message.error(
        error.response?.data?.message ||
        "No internet connection. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    message.destroy();

    // Get all form values
    const values = form.getFieldsValue();

    // Check if any field is empty
    if (!values.email || !values.password) {
      message.error("Please fill all the fields");
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(values.email)) {
      message.error("Enter a valid email");
      return;
    }

    if (values.password.length < 6) {
      message.error("Password must be at least 6 characters");
      return;
    }


    // If all validations pass, submit the form
    form.submit();
  };

  const onFinishFailed = () => {
    // This is kept empty to prevent default Ant Design error messages
  };

  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true);

      provider.setCustomParameters({
        prompt: "select_account",
      });

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const token = await user.getIdToken();

      // console.log("🔥 Firebase token:", token);

      const res = await API.post(
        "/auth/google",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      // console.log("🔥 Backend response:", res.data);

      localStorage.setItem("token", res.data.token);
      const cleanUser = {
        ...res.data.user,
        profileImg:
          res.data.user.loginProvider === "google"
            ? res.data.user.profileImg || null
            : null,
      };

      localStorage.setItem("user", JSON.stringify(cleanUser));
      setUser(cleanUser);

      message.success("Login successful");
      navigate("/");
    } catch (error) {
      // console.error("❌ Google login error:", error);
      message.error("Google login failed");
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <Card className="login-card" variant="borderless">
          <div className="login-header">
            <div className="brand-section">
              <div className="brand-logo">D</div>
              <Title level={3} className="brand-name">Draptor</Title>
            </div>
            <Title level={4} className="login-title">
              Welcome back
            </Title>
            <Text className="login-subtitle">
              Sign in to continue to Draptor
            </Text>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            validateTrigger={[]}
            requiredMark={false}
          >
            <Form.Item
              label="Email"
              name="email"
              className="form-item"
            >
              <Input
                placeholder="Enter your email"
                className="input-field"
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              className="form-item"
            >
              <Input.Password
                placeholder="Enter your password"
                className="input-field"
              />
            </Form.Item>

            <div className="auth-extra">
              <Link to="/forgot-password">Forgot password?</Link>
            </div>

            <Form.Item className="form-item">
              <Button
                type="primary"
                htmlType="button"
                block
                className="login-btn"
                loading={loading}
                onClick={handleSubmit}
              >
                Sign in
              </Button>
            </Form.Item>

            <Divider className="custom-divider">Or</Divider>

            <Button
              block
              className="google-btn"
              loading={googleLoading}
              onClick={handleGoogleLogin}
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="google"
                className="google-icon"
              />
              Continue with Google
            </Button>

            <div className="login-footer">
              <Text className="footer-text">
                Don't have an account? <Link to="/register">Create account</Link>
              </Text>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
}

export default Login;